import { keyframes } from '@emotion/react'
import styled from '@emotion/styled'
import * as AlertDialog from '@radix-ui/react-alert-dialog'

import { Flex } from '../Flex'
import { Headline2 } from '../Typography'

import { Close } from '@/assets/icons'
import { colors } from '@/constants'
import { ModalComponentProps } from '@/typings/Modals'

const contentShow = keyframes`
  from {
    opacity: 0;
    transform: translate(-50%, -48%) scale(0.96);
  }
  to {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
`

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`

const StyledOverlay = styled(AlertDialog.Overlay)`
  background-color: rgba(0, 0, 0, 0.5);
  position: fixed;
  inset: 0;
  z-index: 1000;
  animation: ${fadeIn} 200ms cubic-bezier(0.16, 1, 0.3, 1);
`

const ModalContentWithCross = styled(AlertDialog.Content)`
  display: flex;
  flex-direction: column;
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: ${colors.darkBlue1};
  padding: 1.25rem;
  border-radius: 1.25rem;
  width: 27.5rem;
  z-index: 1001;
  animation: ${contentShow} 300ms cubic-bezier(0.16, 1, 0.3, 1);
  box-shadow:
    0px 10px 38px -10px rgba(22, 23, 24, 0.35),
    0px 10px 20px -15px rgba(22, 23, 24, 0.2);

  &:focus {
    outline: none;
  }
`

const CloseButton = styled(AlertDialog.Cancel)`
  display: flex;
  justify-content: flex-end;
  cursor: pointer;
  opacity: 0.8;
  transition: opacity 0.2s ease;
  animation: ${fadeIn} 300ms 100ms backwards cubic-bezier(0.16, 1, 0.3, 1);
  color: #191c2c;

  &:hover {
    opacity: 1;
  }

  &:focus-visible {
    color: ${colors.blue};
  }
`

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const ModalContent = styled(Flex)`
  flex-direction: column;
  padding: 0 1.25rem 1.25rem 1.25rem;
`

const ModalTitle = styled(AlertDialog.Title)`
  width: 100%;
  text-align: center;
  justify-content: center;
  animation: ${slideUp} 300ms 150ms backwards cubic-bezier(0.16, 1, 0.3, 1);
`

const ModalBody = styled.div`
  animation: ${slideUp} 300ms 200ms backwards cubic-bezier(0.16, 1, 0.3, 1);
`

const ModalFooter = styled.div`
  animation: ${slideUp} 300ms 250ms backwards cubic-bezier(0.16, 1, 0.3, 1);
`

export default function DesktopModal({
  type,
  open,
  setOpen,
  modalHeader,
  modalBody,
  modalFooter,
  onConfirm,
  confirmButtonText = 'OK',
  cancelButtonText = 'Cancel',
  isLoading,
}: ModalComponentProps) {
  return (
    <AlertDialog.Root open={open} onOpenChange={setOpen}>
      <AlertDialog.Portal>
        <StyledOverlay
          onClick={(e) => {
            e.stopPropagation()
            setOpen(false)
          }}
        />
        <ModalContentWithCross>
          <AlertDialog.Description style={{ display: 'none' }}>
            Dialog
          </AlertDialog.Description>
          <CloseButton
            onClick={() => setOpen(false)}
            tabIndex={0}
            role="button"
            aria-label={`Close modal`}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                setOpen(false)
              }
            }}
          >
            <Close />
          </CloseButton>
          <ModalContent>
            <ModalTitle asChild>
              {typeof modalHeader === 'string' ? (
                <Headline2 style={{ textAlign: 'center' }}>
                  {modalHeader}
                </Headline2>
              ) : (
                <Flex style={{ textAlign: 'center' }}>{modalHeader}</Flex>
              )}
            </ModalTitle>

            <ModalBody>{modalBody}</ModalBody>

            <ModalFooter>
              {modalFooter?.({
                onConfirm,
                cancelButtonText,
                confirmButtonText,
                isLoading,
                type,
              })}
            </ModalFooter>
          </ModalContent>
        </ModalContentWithCross>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  )
}
